import {
	chakra,
	Container,
	Flex,
	Input,
	useMultiStyleConfig,
	Box,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
	NewsletterSectionFragment,
	useGetAllProductPagesForNewsletterQuery,
	useGetAllTargetGroupsForNewsletterQuery,
} from "src/__generated__/client.codegen";
import { BackgroundShape } from "src/components/BackgroundShape/BackgroundShape";
import { FormErrorMessageI18n } from "src/components/FormErrorMessageI18n/FormErrorMessageI18n";
import { FormSelect } from "src/components/FormSelect/FormSelect";
import { FormSubmitButton } from "src/components/FormSubmitButton";
import { Hx } from "src/components/Heading/Heading";
import { Loading } from "src/components/Loading/Loading";
import {
	isStructuredText,
	StructuredText,
} from "src/components/StructuredText/StructuredText";
import { FormControl, FormLabel } from "src/components/forms";
import { useTranslatedString } from "src/i18n/hooks";
import { Handshake, Submit } from "src/icons";
import { useGTMSubmitSignup } from "src/lib/gtm";
import { useSiteSettings } from "src/lib/hooks";
import { useNewsletterSignup } from "src/lib/useNewsletterSignup";
import { z } from "src/lib/zod";

type NewsletterProps = Omit<NewsletterSectionFragment, "__typename">;

export const NewsletterSection: React.FC<NewsletterProps> = ({
	topContent,
	bottomContent,
}) => {
	const t = useTranslatedString();
	const styles = useMultiStyleConfig("NewsletterSignup", {
		variant: "targetGroup",
	});
	const trackSuccessfulSignup = useGTMSubmitSignup();
	const { locale } = useSiteSettings();
	const { data: productData, isPending: isPendingProducts } =
		useGetAllProductPagesForNewsletterQuery({
			locale,
		});

	const { data: targetGroupData, isPending: isPendingTargetGroups } =
		useGetAllTargetGroupsForNewsletterQuery({
			locale,
		});

	const form = useForm<{ email: string; formUrl: string }>({
		resolver: zodResolver(
			z.object({
				email: z.string().email(),
				formUrl: z.string().min(1),
			}),
		),
		mode: "onTouched",
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = form;

	const { mutate, isPending, isSuccess } = useNewsletterSignup(() => {
		trackSuccessfulSignup();
		reset();
	});

	if (isPendingProducts || isPendingTargetGroups) {
		<chakra.div __css={styles.wrapper} data-condition={isSuccess}>
			<BackgroundShape offset={55} />
			<Container maxW="container.md" sx={styles.inner}>
				<Loading />
			</Container>
		</chakra.div>;
	}

	if (!productData || !targetGroupData) {
		return null;
	}

	const items = [
		...productData.allProductPages,
		...targetGroupData.allTargetGroups,
		// theoretically this filter's not necessary as we check for "brevoFormUrl: { isPresent: true }" in the query, but this way TS is happy
	].flatMap(({ brevoFormUrl, ...rest }) =>
		brevoFormUrl ? { ...rest, brevoFormUrl } : [],
	);

	return (
		<chakra.div __css={styles.wrapper} data-condition={isSuccess}>
			<BackgroundShape offset={55} />
			<Container maxW="container.md" sx={styles.inner}>
				<chakra.div __css={styles.success}>
					<Hx sx={styles.title}>{t("newsletter.success.title")}</Hx>
					<Handshake w="auto" h="10rem" mb={8} />
					<Box __css={styles.intro}>
						{t("newsletter.success.text")}
					</Box>
				</chakra.div>
				<chakra.div __css={styles.formWrapper}>
					<Box>
						{isStructuredText(topContent) && (
							<StructuredText data={topContent} />
						)}
					</Box>
					<form
						noValidate
						onSubmit={handleSubmit(({ email, formUrl }) =>
							mutate({ email, formUrl }),
						)}
					>
						<Flex sx={styles.form}>
							<FormControl isInvalid={Boolean(errors.email)}>
								<FormLabel htmlFor="email" sx={styles.label}>
									{t("newsletter.email.label")}
								</FormLabel>

								<Input
									{...register("email")}
									id="email"
									data-testid="email"
									placeholder={t(
										"newsletter.email.placeholder",
									)}
									variant="filled"
								/>
								<FormErrorMessageI18n error={errors.email} />
							</FormControl>
							<FormProvider {...form}>
								<FormSelect
									name="formUrl"
									label={t("newsletter.group.label")}
									placeholder={t(
										"newsletter.group.placeholder",
									)}
									isInvalid={Boolean(errors.formUrl)}
									sx={styles.formSelect}
									variant="light"
								>
									{items.map(({ id, name, brevoFormUrl }) => (
										<option key={id} value={brevoFormUrl}>
											{name}
										</option>
									))}
								</FormSelect>
							</FormProvider>
							<FormSubmitButton
								aria-label={t("newsletter.send")}
								data-testid="subscribe"
								sx={styles.submit}
								isLoading={isPending}
								isValid={!errors.email && !errors.formUrl}
								rightIcon={<Submit w={6} h={6} />}
								id="submit_newsletter_form"
							>
								{t("newsletter.send")}
							</FormSubmitButton>
						</Flex>
					</form>
					<Box sx={{ p: { fontSize: "xs" } }}>
						{isStructuredText(bottomContent) && (
							<StructuredText data={bottomContent} />
						)}
					</Box>
				</chakra.div>
			</Container>
		</chakra.div>
	);
};

// 🔬 TBD: Please evaluate
